import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaCheck } from "react-icons/fa";
import img1 from "../StackCard_Scroll/images/image1.png";
import img2 from "../StackCard_Scroll/images/image2.png";
import img3 from "../StackCard_Scroll/images/image3.png";
import img4 from "../StackCard_Scroll/images/image4.png";
import img5 from "../StackCard_Scroll/images/image5.png";
import img6 from "../StackCard_Scroll/images/image6.png";

const HIGHLIGHT = "#1DB954";

const CARD_HEIGHT = 630;
const CARD_STEP = CARD_HEIGHT;
const STICKY_TOP = 90;
const GAP = 24;
const LIFT = 20;

// const renderTitleTwoLines = (title, highlight) => {
//     const idx = title.indexOf(highlight);
//     if (idx === -1) return title;
//     const first = title.slice(0, idx).trim();
//     const second = highlight;
//     const tail = title.slice(idx + highlight.length).trim();
//     return (
//         <>
//             <span style={{ display: "block" }}>
//                 {first}
//                 {tail ? " " + tail : ""}
//             </span>
//             <span style={{ display: "block", marginTop: 4, color: HIGHLIGHT }}>{second}</span>
//         </>
//     );
// };

const renderInlineHighlight = (title, highlight) => {
    const idx = title.indexOf(highlight);
    if (idx === -1) return title;

    const first = title.slice(0, idx);
    const second = highlight;
    const tail = title.slice(idx + highlight.length);

    return (
        <>
            {first}
            <span style={{ color: HIGHLIGHT }}>{second}</span>
            {tail}
        </>
    );
};

const StackCard = () => {
    const CARDS = useMemo(
        () => [
            {
                title: "One-click chat with WhatsApp",
                highlight: " WhatsApp",
                desc: "Start instant conversations from ads or links with a single click.",
                href: "#",
                bullets: [
                    "Jump from ads to chat instantly.",
                    "Open WhatsApp with one tap.",
                    "Reduce drop-offs across funnels.",
                    "Capture leads inside WhatsApp.",
                    "Track chats by ad or source.",
                    "Speed up first response times.",
                    "Boost conversions with direct chat.",
                ],
                img: img1,
            },
            {
                title: "Interactive WhatsApp forms",
                highlight: "WhatsApp",
                desc: "Use smart forms to simplify and speed up customer communication.",
                href: "#",
                bullets: [
                    "Collect details directly in chat.",
                    "Guided steps reduce user errors.",
                    "Mobile-first, frictionless input.",
                    "Auto-validate fields in real time.",
                    "Push data into your CRM safely.",
                    "No redirects or extra pages.",
                    "Faster submissions, better leads.",
                ],
                img: img2,
            },
            {
                title: "Simplify chats using templates",
                highlight: "templates",
                desc: "Streamline replies with ready-to-use templates.",
                href: "#",
                bullets: [
                    "Reply fast with saved messages.",
                    "Keep tone and format consistent.",
                    "Use dynamic fields for personalization.",
                    "Cut manual typing and errors.",
                    "Handle repeats at scale easily.",
                    "Share offers and reminders quickly.",
                    "Accelerate resolution time.",
                ],
                img: img3,
            },
            {
                title: "Accept payments via WhatsApp",
                highlight: " WhatsApp",
                desc: "Enable seamless, secure and instant payments with ease.",
                href: "#",
                bullets: [
                    "Enable in-chat checkout flow.",
                    "Support multiple payment modes.",
                    "Instant confirmations after pay.",
                    "Secure UPI and verified flows.",
                    "No page redirects required.",
                    "Track transactions in real time.",
                    "Simplify refunds and updates.",
                ],
                img: img4,
            },
            {
                title: "In-chat webview on WhatsApp",
                highlight: "WhatsApp",
                desc: "Keep users in flow with built-in browsing.",
                href: "#",
                bullets: [
                    "Open pages inside WhatsApp.",
                    "Avoid browser jumps and drops.",
                    "Load fast for smooth journeys.",
                    "Keep users engaged in one place.",
                    "Complete actions without switching.",
                    "Support catalogs, forms, checkout.",
                    "Improve end-to-end conversion.",
                ],
                img: img5,
            },

            {
                title: "Voice calling integrated in WhatsApp chat",
                highlight: "WhatsApp",
                desc: "Get Instant voice support on WhatsApp.",
                href: "#",
                bullets: [
                    "Switch from chat to voice instantly.",
                    "Offer real-time query resolution.",
                    "Add human touch to customer support.",
                    "No long call waiting or number dialing.",
                    "Secure and encrypted calls.",
                    "Reduce wait times and support costs.",
                    "Improve customer trust and engagement.",
                ],
                img: img6,
            },
        ],
        []
    );

    const containerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [thresholds, setThresholds] = useState([]);

    // Compute thresholds (robust against layout shifts)
    const computeThresholds = () => {
        const el = containerRef.current;
        if (!el) return;
        const top = el.getBoundingClientRect().top + window.scrollY;
        const starts = [];
        let running = top + STICKY_TOP;
        for (let i = 0; i < CARDS.length; i++) {
            starts.push(running);
            running += CARD_STEP + GAP;
        }
        setThresholds(starts);
    };

    useEffect(() => {
        // First frame, after paint
        const rafId = requestAnimationFrame(computeThresholds);

        // Recompute when page fully loads (images/css)
        const onLoad = () => computeThresholds();
        window.addEventListener("load", onLoad, { once: true });

        // Recompute when webfonts finish
        if (document.fonts?.ready) {
            document.fonts.ready.then(computeThresholds).catch(() => { });
        }

        // Recompute on container or page size changes
        const ro = new ResizeObserver(() => computeThresholds());
        ro.observe(document.body);

        const onResize = () => computeThresholds();
        window.addEventListener("resize", onResize);

        return () => {
            cancelAnimationFrame(rafId);
            window.removeEventListener("resize", onResize);
            ro.disconnect();
        };
    }, [CARDS.length]);

    // rAF-throttled scroll handler
    const scrollRAF = useRef(0);
    useEffect(() => {
        const onScroll = () => {
            if (!thresholds.length) return;
            if (scrollRAF.current) return;
            scrollRAF.current = requestAnimationFrame(() => {
                scrollRAF.current = 0;
                const y = window.scrollY + STICKY_TOP + 1;
                let idx = 0;
                for (let i = 0; i < thresholds.length; i++) {
                    if (y >= thresholds[i]) idx = i;
                    else break;
                }
                setActiveIndex(idx);
            });
        };
        // Run once immediately (in case user is already scrolled)
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            if (scrollRAF.current) cancelAnimationFrame(scrollRAF.current);
            window.removeEventListener("scroll", onScroll);
        };
    }, [thresholds]);

    return (
        <section
            className="py-5 position-relative"
            style={{
                overflow: "visible",
                background: "linear-gradient(90deg, #ECF4FF 0%, #F6F9FF 100%)",
                marginTop: 90,
            }}
        >
            <div
                className="px-5 x-xxl-5 px-6 mx-auto position-relative"
                style={{ maxWidth: "74%", zIndex: 1 }}
            >
                <div>
                    <h2
                        style={{
                            fontSize: "2.5rem",
                            fontFamily: "Inter",
                            fontWeight: 700,
                            color: "#242121ff",
                            lineHeight: 1.2,
                        }}
                    >
                        Easy solutions on{" "}
                        <span style={{ color: "#1DB954", fontFamily: "'Inter'" }}>
                            WhatsApp
                        </span>
                        <p
                            className="mt-2"
                            style={{
                                marginTop: -2,
                                color: "#413e3eff",
                                fontSize: "1.1rem",
                                fontFamily: "Poppins",
                                fontWeight: 300,
                                lineHeight: 1.2,
                            }}
                        >
                            Manage ads, forms and payments in one chat-faster, simpler and more
                            connected.
                        </p>
                    </h2>
                </div>

                {/* STACK */}
                <div
                    ref={containerRef}
                    style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        gap: GAP,
                        marginTop: "-70px",
                        paddingTop: STICKY_TOP,
                        paddingBottom: 20,
                        isolation: "isolate",
                        overflow: "visible",
                    }}
                >
                    {CARDS.map((c, i) => {
                        const isPast = i < activeIndex;
                        const isCurrent = i === activeIndex;

                        const liftAmount = isPast ? LIFT * (activeIndex - i) : 0;
                        const visualFilter = isPast ? "saturate(0.92) brightness(0.98)" : "none";
                        const topEdgeOffset = isPast ? (activeIndex - i) * 4 : 0;
                        const scaleAmount = isPast ? 1 - (activeIndex - i) * 0.04 : 1;

                        return (
                            <div key={i} style={{ position: "sticky", top: STICKY_TOP, zIndex: i + 1 }}>
                                <div
                                    style={{
                                        width: `100%`,
                                        margin: "0 auto",
                                        transition: "width 260ms ease-out",
                                        willChange: "width",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            gap: 32,
                                            padding: "50px 36px 50px",
                                            minHeight: CARD_HEIGHT,
                                            height: CARD_HEIGHT,
                                            background:
                                                "linear-gradient(135deg, rgba(255,255,255,0.78), rgba(255,255,255,0.58))",
                                            backdropFilter: "blur(10px) saturate(160%)",
                                            WebkitBackdropFilter: "blur(10px) saturate(160%)",
                                            borderRadius: 16,
                                            border: "1px solid rgba(255,255,255,0.7)",
                                            boxShadow: isCurrent
                                                ? "0 16px 32px rgba(16,24,40,0.10), 0 1px 0 rgba(255,255,255,0.6) inset"
                                                : "0 10px 24px rgba(16,24,40,0.08), 0 1px 0 rgba(255,255,255,0.6) inset",
                                            overflow: "hidden",
                                            transform: `translateY(${-liftAmount - topEdgeOffset}px) scale(${scaleAmount})`,
                                            opacity: 1,
                                            filter: visualFilter,
                                            backfaceVisibility: "hidden",
                                            transformStyle: "preserve-3d",
                                            transition:
                                                "transform 220ms ease-out, filter 180ms ease-out, box-shadow 180ms ease-out",
                                            willChange: "transform, filter",
                                        }}
                                    >
                                        {/* Left side with attached white background panel */}
                                        <div
                                            style={{
                                                flex: "0 0 30%",
                                                maxWidth: "30%",
                                                marginLeft: "40px",
                                                position: "relative",
                                                display: "flex",
                                            }}
                                        >
                                            <div
                                                style={{
                                                    position: "absolute",
                                                    top: 0,
                                                    left: "-40px",
                                                    right: -84,
                                                    bottom: -20,
                                                    background: "#FFFFFF",
                                                    borderRadius: "12px 0 0 12px",
                                                    zIndex: 0,
                                                }}
                                            ></div>

                                            <div
                                                style={{
                                                    position: "relative",
                                                    zIndex: 1,
                                                    padding: "24px 28px 28px 40px",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "flex-start",
                                                    width: "100%",
                                                }}
                                            >
                                                <h3
                                                    style={{
                                                        marginTop: "10px",
                                                        fontSize: "28px",
                                                        color: "#242121ff",
                                                        fontWeight: 600,
                                                        fontFamily: "Lato",
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {c.title === "Voice calling integrated in WhatsApp chat" ? (
                                                        <>
                                                            <span style={{ display: "block" }}>
                                                                Voice calling integrated
                                                            </span>
                                                            <span style={{ display: "block", marginTop: 4 }}>
                                                                in <span style={{ color: HIGHLIGHT }}>WhatsApp</span> chat
                                                            </span>
                                                        </>
                                                    ) : (
                                                        renderInlineHighlight(c.title, c.highlight)
                                                    )}
                                                </h3>

                                                <p
                                                    style={{
                                                        marginTop: 8,
                                                        marginBottom: c.bullets?.length ? 10 : 16,
                                                        fontSize: "1.15rem",
                                                        fontFamily: "Lato",
                                                        fontWeight: 400,
                                                        color: "#413e3eff",
                                                        lineHeight: 1.2,
                                                        maxWidth: "100%",
                                                    }}
                                                >
                                                    {c.desc}
                                                </p>

                                                {c.bullets?.length ? (
                                                    <ul
                                                        style={{
                                                            listStyle: "none",
                                                            padding: 0,
                                                            margin: "14px 0 28px 0",
                                                            display: "grid",
                                                            rowGap: 10,
                                                            maxWidth: "100%",
                                                        }}
                                                    >
                                                        {c.bullets.map((b, idx) => (
                                                            <li
                                                                key={idx}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "flex-start",
                                                                    gap: 12,
                                                                    color: "#413e3eff",
                                                                    fontSize: '1rem',
                                                                    fontFamily: "Lato",
                                                                    fontWeight: 350,
                                                                    lineHeight: 1.35,
                                                                }}

                                                            >
                                                                <span
                                                                    style={{
                                                                        marginTop: 4,
                                                                        width: 22,
                                                                        height: 20,
                                                                        borderRadius: "50%",
                                                                        background: "#EEF4FF",
                                                                        border: "1px solid #D6E2FF",
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        flex: "0 0 22px",
                                                                    }}
                                                                    aria-hidden
                                                                >
                                                                    <FaCheck size={10} color="#054E91" />
                                                                </span>
                                                                {/* <span style={{ color: "#394150" }}>{b}</span> */}
                                                                <span
                                                                    style={{
                                                                        color: "#394150",
                                                                        whiteSpace: "nowrap",      // line break mat hone do
                                                                        overflow: "hidden",        // agar jagah kam ho to...
                                                                        textOverflow: "ellipsis",  // ...end me "…" dikhaye
                                                                    }}
                                                                >
                                                                    {b}
                                                                </span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                ) : null}

                                                <a
                                                    href={c.href}
                                                    style={{
                                                        marginTop: 0,
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        fontSize: 18,
                                                        fontWeight: 600,
                                                        color: "#054E91",
                                                        textDecoration: "none",
                                                        fontFamily: 'Inter'
                                                    }}
                                                >
                                                    Know more
                                                    <span aria-hidden style={{ marginLeft: "2px" }}>➜</span>
                                                </a>
                                            </div>
                                        </div>

                                        {/* Right side */}
                                        <div
                                            style={{
                                                flex: "0 0 70%",
                                                maxWidth: "60%",
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                alignItems: "stretch",
                                            }}
                                        >
                                            <img
                                                src={c.img}
                                                alt="Preview"
                                                loading="eager"
                                                fetchpriority="high"
                                                decoding="async"
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    display: "block",
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default StackCard;
